import { CHUNK_BYTES, type Zone } from '@fh/shared/media'

import { api } from '../api.js'
import { t } from '../i18n/t.js'

/**
 * Upload in Blöcken, mit Wiederaufnahme (Anf. 76).
 *
 * Geschrieben für die Verbindung, die es tatsächlich gibt: im Zug, im Keller,
 * beim Wechsel von WLAN auf Mobilfunk. Deshalb:
 *
 *  * **Ein Block auf einmal, nicht die Datei.** Bricht die Verbindung, ist ein
 *    Block verloren, nicht die 400 MB davor.
 *  * **Der Server sagt, was fehlt.** Nach einem Abbruch wird nicht geraten und
 *    nicht von vorn begonnen — der Client fragt nach und holt gezielt nach.
 *  * **Wiederholen mit wachsender Pause.** Ein sofortiger zweiter Versuch
 *    trifft dieselbe kaputte Verbindung. Vier Versuche über rund zwölf
 *    Sekunden überbrücken einen Funklochwechsel; danach ist es kein Aussetzer
 *    mehr, sondern ein Ausfall, und der gehört gemeldet.
 */

export interface UploadFortschritt {
  gesendet: number
  gesamt: number
  /** 0..1 — für den Balken. */
  anteil: number
}

export interface HochgeladenerBlob {
  id: string
  mime: string
  byteSize: number
  scanStatus: string
}

const VERSUCHE = 4

export class UploadAbgebrochen extends Error {
  constructor() {
    super(t('Der Upload wurde abgebrochen.'))
    this.name = 'UploadAbgebrochen'
  }
}

/**
 * Lädt einen Blob hoch und liefert seine ID.
 *
 * `signal` bricht sauber ab: Der angefangene Upload bleibt auf dem Server
 * liegen und verfällt dort nach 24 Stunden von selbst. Nichts aufzuräumen ist
 * hier richtig — ein Abbruch passiert oft, weil das Netz weg ist, und dann
 * käme auch ein Aufräum-Aufruf nicht mehr durch.
 */
export async function hochladen(
  daten: Blob,
  opts: {
    zone: Zone
    thumbnail?: boolean
    onFortschritt?: (f: UploadFortschritt) => void
    signal?: AbortSignal
  },
): Promise<HochgeladenerBlob> {
  const gesamt = daten.size
  const start = await api.post<{ uploadId: string; chunkCount: number }>('/blobs/init', {
    zone: opts.zone,
    byteSize: gesamt,
    ...(opts.thumbnail ? { thumbnail: true } : {}),
  })

  let offen = Array.from({ length: start.chunkCount }, (_, i) => i)
  let gesendet = 0

  const melden = () => opts.onFortschritt?.({
    gesendet, gesamt, anteil: gesamt === 0 ? 1 : Math.min(1, gesendet / gesamt),
  })
  melden()

  for (let runde = 0; runde < VERSUCHE && offen.length > 0; runde++) {
    if (runde > 0) {
      // Der Server ist die Quelle der Wahrheit darüber, was angekommen ist.
      // Ein Block kann durchgegangen sein, obwohl die Antwort verlorenging.
      const stand = await api.get<{ missing: number[] }>(`/blobs/${start.uploadId}/status`)
      offen = stand.missing
      gesendet = (start.chunkCount - offen.length) * CHUNK_BYTES
      melden()
      await warten(500 * 2 ** (runde - 1), opts.signal)
    }

    const gescheitert: number[] = []
    for (const n of offen) {
      if (opts.signal?.aborted) throw new UploadAbgebrochen()
      const stueck = daten.slice(n * CHUNK_BYTES, Math.min((n + 1) * CHUNK_BYTES, gesamt))
      try {
        await api.putRaw(
          `/blobs/${start.uploadId}/chunk?n=${n}`,
          await stueck.arrayBuffer(),
          opts.signal ? { signal: opts.signal } : {},
        )
        gesendet += stueck.size
        melden()
      } catch (e) {
        if (opts.signal?.aborted) throw new UploadAbgebrochen()
        gescheitert.push(n)
        // Weitermachen statt abbrechen: Auf einer wackligen Verbindung geht
        // selten alles kaputt, meist nur einzelne Blöcke. Wer beim ersten
        // Fehler aufgibt, wiederholt am Ende mehr als nötig.
        void e
      }
    }
    offen = gescheitert
  }

  if (offen.length > 0) {
    throw new Error(`${offen.length} Teile konnten nicht gesendet werden. Bist du online?`)
  }

  return api.post<HochgeladenerBlob>(`/blobs/${start.uploadId}/finish`, {})
}

function warten(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => {
      clearTimeout(t)
      reject(new UploadAbgebrochen())
    }, { once: true })
  })
}

/**
 * Kurzlebiger Download-Link.
 *
 * Wird bewusst NICHT zwischengespeichert: Er gilt fünf Minuten, und ein
 * abgelaufener Link aus einem Cache erzeugt genau den Fehler, den niemand
 * versteht („war doch eben noch da").
 */
export async function downloadUrl(blobId: string): Promise<string> {
  return (await api.get<{ url: string }>(`/blobs/${blobId}/url`)).url
}
